"use client";

import { useContext, useEffect } from "react";
import { AppContext } from "@/context/AppContext";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useLocalePath } from "@/i18n/LangContext";

interface AboutIntroProps {
  intro: string[];
  buttons: {
    work: string;
    contact: string;
  };
}

export function AboutIntro({ intro, buttons }: AboutIntroProps) {
  const { setActivePage } = useContext(AppContext);
  const lp = useLocalePath();

  useEffect(() => {
    setActivePage("about");
  }, [setActivePage]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 50, damping: 15 }
    }
  };

  return (
    <motion.div
      className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants} className="order-2 md:order-1">
        {intro.map((paragraph, index) => (
          <motion.p key={index} className="text-md md:text-lg mb-6 text-content">
            {paragraph}
          </motion.p>
        ))}

        <motion.div variants={itemVariants} className="flex gap-6 mb-8">
          <Link href={lp('/work')}>
            <motion.button
              className="px-6 py-3 rounded-lg bg-[#0C2019] text-[#88ff88] border border-[#88ff88]/30 font-medium"
              whileHover={{
                scale: 1.05,
                backgroundColor: "rgba(15, 50, 40, 1)"
              }}
              whileTap={{ scale: 0.97 }}
            >
              {buttons.work}
            </motion.button>
          </Link>
          <Link href={lp('/contact')}>
            <motion.button
              className="px-6 py-3 rounded-lg bg-[#0C2019] text-[#88ff88] border border-[#88ff88]/30 font-medium"
              whileHover={{
                scale: 1.05,
                backgroundColor: "rgba(15, 50, 40, 1)"
              }}
              whileTap={{ scale: 0.97 }}
            >
              {buttons.contact}
            </motion.button>
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        className="order-1 md:order-2 flex justify-center"
        variants={itemVariants}
      >
        <motion.div
          className="relative w-64 h-64 md:w-80 md:h-80 overflow-hidden rounded-xl border-4 border-[#88ff88]/30"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{
            opacity: 1,
            scale: 1,
            transition: {
              delay: 0.3,
              duration: 0.8,
              ease: [0.2, 0.65, 0.3, 0.9]
            }
          }}
        >
          <Image
            src="/assets/img/robin_square.webp"
            alt="Robin Wichmann"
            loading={"eager"}
            fill
            sizes="(max-width: 768px) 16rem, 20rem"
            style={{ objectFit: "cover" }}
            className="transition-all duration-700 hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0C2019]/70 to-transparent"></div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}